import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
} from "@/hooks/api/useDepartments";
import type { DepartmentDTO } from "@arna-med/shared";
import { Plus, Pencil, Trash2, ChevronRight } from "lucide-react";

type DepartmentForm = {
  name: string;
  description: string;
  parentId: string | null;
};

const emptyForm: DepartmentForm = { name: "", description: "", parentId: null };

function DepartmentFormDialog({
  open,
  onClose,
  initial,
  onSubmit,
  isPending,
  departments,
  editingId,
}: {
  open: boolean;
  onClose: () => void;
  initial?: DepartmentForm;
  onSubmit: (data: DepartmentForm) => void;
  isPending: boolean;
  departments: DepartmentDTO[];
  editingId?: string;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<DepartmentForm>(initial ?? emptyForm);

  useEffect(() => {
    if (open) setForm(initial ?? emptyForm);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof DepartmentForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value || (k === "parentId" ? null : e.target.value) }));

  const availableParents = departments.filter((d) => d.id !== editingId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingId ? t("departments.editDepartment") : t("departments.addDepartment")}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label>{t("departments.name")}</Label>
            <Input value={form.name} onChange={set("name")} required />
          </div>
          <div className="space-y-2">
            <Label>{t("departments.description")}</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full min-h-[72px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              placeholder={t("departments.descriptionPlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("departments.parentDepartment")}</Label>
            <select
              value={form.parentId ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, parentId: e.target.value || null }))
              }
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">{t("departments.noParent")}</option>
              {availableParents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function buildTree(
  departments: DepartmentDTO[],
  parentId: string | null = null,
): (DepartmentDTO & { children: DepartmentDTO[] })[] {
  return departments
    .filter((d) => d.parentId === parentId)
    .map((d) => ({ ...d, children: buildTree(departments, d.id) as DepartmentDTO[] }));
}

type DeptNode = DepartmentDTO & { children: DepartmentDTO[] };

function DepartmentRow({
  node,
  depth,
  allDepts,
  onEdit,
  onDelete,
}: {
  node: DeptNode;
  depth: number;
  allDepts: DepartmentDTO[];
  onEdit: (dept: DepartmentDTO) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const children = node.children as DeptNode[];

  return (
    <>
      <tr className="border-b last:border-0 hover:bg-muted/30 transition-colors">
        <td className="px-4 py-3">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 20}px` }}>
            {depth > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
            <span className="font-medium text-sm">{node.name}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-muted-foreground">
          {node.description ?? "—"}
        </td>
        <td className="px-4 py-3 text-sm text-muted-foreground">
          {node.parentId
            ? (allDepts.find((d) => d.id === node.parentId)?.name ?? "—")
            : "—"}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 justify-end">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => onEdit(node)}
              title={t("common.edit")}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => setConfirmId(node.id)}
              title={t("common.delete")}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <ConfirmDialog
            open={confirmId === node.id}
            onConfirm={() => {
              onDelete(node.id);
              setConfirmId(null);
            }}
            onOpenChange={(v) => { if (!v) setConfirmId(null); }}
            title={t("departments.confirmDelete")}
            description={t("departments.confirmDeleteDesc", { name: node.name })}
          />
        </td>
      </tr>
      {children.map((child) => (
        <DepartmentRow
          key={child.id}
          node={child as DeptNode}
          depth={depth + 1}
          allDepts={allDepts}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}

export default function Departments() {
  const { t } = useTranslation();
  const { data: departments = [], isLoading } = useDepartments();
  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();
  const deleteMutation = useDeleteDepartment();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<DepartmentDTO | null>(null);

  const openCreate = () => {
    setEditingDept(null);
    setDialogOpen(true);
  };

  const openEdit = (dept: DepartmentDTO) => {
    setEditingDept(dept);
    setDialogOpen(true);
  };

  const handleSubmit = (form: DepartmentForm) => {
    const payload = {
      name: form.name,
      description: form.description || null,
      parentId: form.parentId,
    };
    if (editingDept) {
      updateMutation.mutate(
        { id: editingDept.id, data: payload },
        { onSuccess: () => setDialogOpen(false) },
      );
    } else {
      createMutation.mutate(payload, { onSuccess: () => setDialogOpen(false) });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const tree = buildTree(departments);

  const initialForm: DepartmentForm = editingDept
    ? {
        name: editingDept.name,
        description: editingDept.description ?? "",
        parentId: editingDept.parentId,
      }
    : emptyForm;

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t("departments.title")}</h1>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          {t("departments.addDepartment")}
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : departments.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("departments.empty")}</p>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  {t("departments.name")}
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  {t("departments.description")}
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  {t("departments.parentDepartment")}
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {tree.map((node) => (
                <DepartmentRow
                  key={node.id}
                  node={node as DeptNode}
                  depth={0}
                  allDepts={departments}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DepartmentFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={initialForm}
        onSubmit={handleSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
        departments={departments}
        editingId={editingDept?.id}
      />
    </Layout>
  );
}
